import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from 'firebase-config';
import { AppUser } from 'services/api/user';
import { fetchUserFromBackend, updateUserProfile } from 'services/api/user';

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          let appUser = await fetchUserFromBackend(firebaseUser);

          // Sync displayName if it's different
          if (firebaseUser.displayName && firebaseUser.displayName !== appUser.displayName) {
            try {
              await updateUserProfile(firebaseUser.uid, firebaseUser.displayName);
              // Update the local state to reflect the change immediately
              appUser = { ...appUser, displayName: firebaseUser.displayName };
            } catch (syncError) {
              console.error("Failed to sync displayName:", syncError);
              // Continue with the potentially stale appUser data
            }
          }
          
          setUser(appUser);
        } catch (error) {
          console.error("Failed to fetch user profile, logging out.", error);
          setUser(null);
          // Optionally, sign out from firebase as well
          // auth.signOut();
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}; 